import { useState, useRef, useCallback, useEffect } from 'react';
import { float32ToInt16 } from '@/lib/audio/wav-encoder';
import type { AudioSource, StreamStatus } from './useMixedAudioRecorder';

// Re-export types for convenience
export type { AudioSource, StreamStatus };

export interface UsePcmRecorderReturn {
  startRecording: () => Promise<boolean>;
  stopRecording: () => void;
  audioChunks: Blob[];
  error: Error | null;
  isRecording: boolean;
  micStatus: StreamStatus;
  tabStatus: StreamStatus;
  tabLabel: string | null;
}

export interface UsePcmRecorderOptions {
  audioSource: AudioSource;
  timeslice?: number;
  tabStream?: MediaStream | null;
}

const PCM_SAMPLE_RATE = 16000;
const PCM_CHANNELS = 1;
const DEFAULT_CHUNK_MS = 500;

function toPcmBlob(samples: Float32Array): Blob {
  const int16Data = float32ToInt16(samples);
  const pcmBuffer = new ArrayBuffer(int16Data.byteLength);
  const pcmView = new Uint8Array(pcmBuffer);
  pcmView.set(new Uint8Array(int16Data.buffer, int16Data.byteOffset, int16Data.byteLength));
  return new Blob([pcmBuffer], {
    type: `audio/pcm;rate=${PCM_SAMPLE_RATE};channels=${PCM_CHANNELS}`,
  });
}

export function usePcmRecorder(options: UsePcmRecorderOptions): UsePcmRecorderReturn {
  const { audioSource, timeslice = DEFAULT_CHUNK_MS, tabStream: selectedTabStream = null } = options;

  const [audioChunks, setAudioChunks] = useState<Blob[]>([]);
  const [error, setError] = useState<Error | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [micStatus, setMicStatus] = useState<StreamStatus>('inactive');
  const [tabStatus, setTabStatus] = useState<StreamStatus>('inactive');
  const [tabLabel, setTabLabel] = useState<string | null>(null);

  const audioContextRef = useRef<AudioContext | null>(null);
  const workletNodeRef = useRef<AudioWorkletNode | null>(null);
  const micStreamRef = useRef<MediaStream | null>(null);
  const tabStreamRef = useRef<MediaStream | null>(null);
  const tabStreamOwnedRef = useRef(false);
  const isStartingRef = useRef(false);

  useEffect(() => {
    if (selectedTabStream) {
      const tabLabelFromTrack =
        selectedTabStream.getVideoTracks()[0]?.label || selectedTabStream.getAudioTracks()[0]?.label || null;
      setTabLabel(tabLabelFromTrack && tabLabelFromTrack.trim() ? tabLabelFromTrack : null);
      return;
    }
    if (audioSource !== 'tab' && audioSource !== 'both') {
      setTabLabel(null);
    }
  }, [selectedTabStream, audioSource]);

  const handleWorkletMessage = useCallback((event: MessageEvent<Float32Array>) => {
    const pcmData = event.data;
    if (!(pcmData instanceof Float32Array) || pcmData.length === 0) {
      return;
    }
    const pcmBlob = toPcmBlob(pcmData);
    setAudioChunks((prev) => [...prev, pcmBlob]);
  }, []);

  const cleanupStreams = useCallback(() => {
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((track) => track.stop());
      micStreamRef.current = null;
      setMicStatus('inactive');
    }
    if (tabStreamRef.current && tabStreamOwnedRef.current) {
      tabStreamRef.current.getTracks().forEach((track) => track.stop());
    }
    tabStreamRef.current = null;
    tabStreamOwnedRef.current = false;
    setTabStatus('inactive');
    if (!selectedTabStream) {
      setTabLabel(null);
    }
  }, [selectedTabStream]);

  const cleanupAudioContext = useCallback(() => {
    if (workletNodeRef.current) {
      workletNodeRef.current.port.onmessage = null;
      workletNodeRef.current.disconnect();
      workletNodeRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => undefined);
      audioContextRef.current = null;
    }
  }, []);

  const startRecording = useCallback(async () => {
    if (isRecording || isStartingRef.current) {
      return false;
    }
    isStartingRef.current = true;

    if (typeof window === 'undefined') {
      setError(new Error('Window is not defined (SSR environment)'));
      isStartingRef.current = false;
      return false;
    }

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setError(new Error('getUserMedia is not supported in this browser'));
      isStartingRef.current = false;
      return false;
    }

    if (typeof AudioWorklet === 'undefined' || typeof AudioWorkletNode === 'undefined') {
      setError(new Error('AudioWorklet is not supported in this browser'));
      isStartingRef.current = false;
      return false;
    }

    try {
      setAudioChunks([]);
      setError(null);

      const audioContext = new AudioContext({ sampleRate: PCM_SAMPLE_RATE });
      audioContextRef.current = audioContext;
      await audioContext.audioWorklet.addModule('/audio-processor.worklet.js');

      let finalStream: MediaStream;

      if (audioSource === 'mic') {
        const micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        micStreamRef.current = micStream;
        setMicStatus('active');
        finalStream = micStream;
      } else if (audioSource === 'tab') {
        const tabStream = selectedTabStream;
        if (!tabStream) {
          throw new Error('システム音声が未選択です。入力先を選択してください。');
        }
        const tabLabelFromTrack =
          tabStream.getVideoTracks()[0]?.label || tabStream.getAudioTracks()[0]?.label || null;
        setTabLabel(tabLabelFromTrack && tabLabelFromTrack.trim() ? tabLabelFromTrack : null);
        tabStreamRef.current = tabStream;
        tabStreamOwnedRef.current = false;
        setTabStatus('active');
        finalStream = tabStream;
      } else {
        let micStream: MediaStream | null = null;
        let tabStream: MediaStream | null = null;

        try {
          micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
          micStreamRef.current = micStream;
          setMicStatus('active');
        } catch {
          setMicStatus('denied');
        }

        tabStream = selectedTabStream;
        if (tabStream) {
          const tabLabelFromTrack =
            tabStream.getVideoTracks()[0]?.label || tabStream.getAudioTracks()[0]?.label || null;
          setTabLabel(tabLabelFromTrack && tabLabelFromTrack.trim() ? tabLabelFromTrack : null);
          tabStreamRef.current = tabStream;
          tabStreamOwnedRef.current = false;
          setTabStatus('active');
        } else {
          setTabStatus('denied');
          setTabLabel(null);
        }

        if (!micStream && !tabStream) {
          throw new Error('マイクとシステム音声の両方の権限が拒否されました');
        }

        if (!micStream && tabStream) {
          finalStream = tabStream;
        } else if (micStream && !tabStream) {
          finalStream = micStream;
        } else if (micStream && tabStream) {
          const micSource = audioContext.createMediaStreamSource(micStream);
          const tabSource = audioContext.createMediaStreamSource(tabStream);
          const micGain = audioContext.createGain();
          const tabGain = audioContext.createGain();
          micGain.gain.value = 1.0;
          tabGain.gain.value = 1.0;
          const destination = audioContext.createMediaStreamDestination();
          micSource.connect(micGain).connect(destination);
          tabSource.connect(tabGain).connect(destination);
          finalStream = destination.stream;
        } else {
          throw new Error('Unreachable code');
        }
      }

      const chunkMs = Number.isFinite(timeslice) && timeslice > 0 ? Math.floor(timeslice) : DEFAULT_CHUNK_MS;
      const workletNode = new AudioWorkletNode(audioContext, 'audio-processor', {
        processorOptions: {
          chunkDurationMs: chunkMs,
        },
      });
      workletNodeRef.current = workletNode;
      workletNode.port.onmessage = handleWorkletMessage;

      const source = audioContext.createMediaStreamSource(finalStream);
      source.connect(workletNode);
      workletNode.connect(audioContext.destination);

      setIsRecording(true);
      return true;
    } catch (err) {
      const nextError =
        err instanceof DOMException || err instanceof Error ? err : new Error(String(err));
      setError(nextError);
      setIsRecording(false);
      cleanupStreams();
      cleanupAudioContext();
      return false;
    } finally {
      isStartingRef.current = false;
    }
  }, [
    audioSource,
    cleanupAudioContext,
    cleanupStreams,
    handleWorkletMessage,
    isRecording,
    timeslice,
    selectedTabStream,
  ]);

  const stopRecording = useCallback(() => {
    if (!isRecording) {
      return;
    }
    cleanupStreams();
    cleanupAudioContext();
    setIsRecording(false);
  }, [cleanupAudioContext, cleanupStreams, isRecording]);

  useEffect(() => {
    return () => {
      cleanupStreams();
      cleanupAudioContext();
    };
  }, [cleanupAudioContext, cleanupStreams]);

  return {
    startRecording,
    stopRecording,
    audioChunks,
    error,
    isRecording,
    micStatus,
    tabStatus,
    tabLabel,
  };
}
